// =========================
// Onboarding — COE-001 Controlled Onboarding Entry Experience
//
// A PUBLIC, low-friction entry page that separates the controlled-onboarding
// cohort from the public waitlist funnel:
//
//   • Public visitor  → "/"           → Join Waitlist   (Landing, unchanged)
//   • Invited user     → "/onboarding" → learn → Activate Invitation → Google
//                                        OAuth → cohort gate (/auth/callback)
//                                        → application
//
// This page explains Rentava BEFORE authentication, then offers two CTAs:
//   • Primary   "Continue to Login"        → /login (existing sign-in surface)
//   • Secondary "Activate Your Invitation" → the EXISTING gated Google OAuth
//                                            flow (signInWithOAuth('google'))
//
// Public Access Lockdown is preserved: there is NO public account-creation /
// email-registration surface here. "Sign up" semantics are invitation
// activation, not public registration — every OAuth session is still
// cohort-gated at /auth/callback (lib/onboardingAllowlist, fail-closed). The
// page itself enforces nothing and bypasses nothing; the allowlist remains the
// hard boundary.
//
// Public route → renders WITHOUT the Layout shell (self-contained, like Login
// / Landing). Design-system tokens only (no hex — CLAUDE.md). Responsive,
// accessible single-column content within a bounded container.
// =========================

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { track } from '../lib/analytics';
import { trackFunnel, getPlanIntent, FUNNEL_EVENTS } from '../lib/attribution';
import { TRIAL_LIMITS } from '../lib/entitlements';

// Mirrors Login.js — the /auth/callback page reads this back once the OAuth
// session resolves so the cohort-gated user lands in the right place.
const POST_AUTH_REDIRECT_KEY = 'rv-str-postauth-redirect';

const SIGNALS = [
  {
    label: 'Pursue',
    body: 'The signals are strong — this opportunity is worth a closer look.',
  },
  {
    label: 'Investigate',
    body: 'The signals are mixed — gather more information before committing time.',
  },
  {
    label: 'Reject',
    body: 'The signals are weak or a gate fails — likely not worth pursuing now.',
  },
];

const EXPECTATIONS = [
  {
    title: 'Invite-only access',
    body: 'Rentava is in a controlled onboarding phase. Access is limited to the ' +
      'invited cohort, so the experience stays focused while we learn together.',
  },
  {
    title: 'Early-access program',
    body: 'You are among the first to use Rentava. You get a direct line to the ' +
      'team and an outsized voice in where the product goes next.',
  },
  {
    title: 'Feedback encouraged',
    body: 'Tell us what works and what does not. Your feedback shapes the roadmap — ' +
      'report a bug, request a feature, or ask a question any time.',
  },
  {
    title: 'Features may evolve',
    body: 'This is early access, so capabilities will evolve and may change as we ' +
      'refine the platform around what the cohort needs.',
  },
];

export default function Onboarding() {
  const { signInWithOAuth, isConfigured } = useAuth();
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  useEffect(() => {
    track('onboarding_view', { source: 'controlled_onboarding_entry' });
    // BL-038 Story 38.4 — record the onboarding view as a funnel stage, carrying
    // the plan intent the visitor expressed on /pricing (null when they arrived
    // directly). Advisory instrumentation only.
    trackFunnel(FUNNEL_EVENTS.ONBOARDING_VIEW, { plan_intent: getPlanIntent() || null });
  }, []);

  async function handleActivate() {
    setError('');
    setBusy(true);
    track('onboarding_activate_invite', { provider: 'google' });

    // Preserve the post-auth destination across the OAuth round-trip — the
    // /auth/callback cohort gate reads this back once the session resolves.
    try {
      window.sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, '/dashboard');
    } catch {
      // sessionStorage may be unavailable (private mode); the callback falls
      // back to its default destination.
    }

    try {
      const { error: oauthError } = await signInWithOAuth('google');
      if (oauthError) {
        setError(oauthError.message || 'Unable to start invitation activation.');
        setBusy(false);
      }
      // On success the browser is already navigating to Google — nothing left
      // to do here.
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  }

  // ── shared token styles ────────────────────────────────────────────────
  const sectionStyle = {
    display: 'grid',
    gap: 'var(--space-3)',
    padding: 'var(--space-5)',
  };
  const h2Style = {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-xl)',
    fontWeight: 'var(--weight-bold)',
    color: 'var(--color-white)',
    margin: 0,
  };
  const pStyle = {
    fontSize: 'var(--text-base)',
    color: 'var(--color-steel-lt)',
    lineHeight: 'var(--leading-normal)',
    margin: 0,
  };
  const itemStyle = {
    display: 'grid',
    gap: 'var(--space-1)',
    padding: 'var(--space-2) var(--space-4)',
  };

  return (
    <div
      style={{
        background: 'var(--color-navy)',
        minHeight: '100vh',
        padding: 'var(--space-6)',
        color: 'var(--color-white)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <main style={{ maxWidth: 760, margin: '0 auto', display: 'grid', gap: 'var(--space-5)' }}>
        {/* ── Header / brand ────────────────────────────────────────── */}
        <header style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-md)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-wide)',
              textTransform: 'uppercase',
              color: 'var(--color-teal)',
            }}
          >
            Rentava
          </span>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 'var(--weight-black)',
              letterSpacing: 'var(--tracking-tight)',
              textTransform: 'uppercase',
              margin: 0,
            }}
          >
            Welcome to your invitation
          </h1>
          <p style={{ ...pStyle, fontSize: 'var(--text-md)' }}>
            You&apos;ve been invited to Rentava&apos;s controlled onboarding — Opportunity
            Intelligence for RV park and short-term rental (STR) investors. Take a moment to
            learn what Rentava does, then activate your invitation.
          </p>
        </header>

        {/* ── Primary activation CTAs (hero) ────────────────────────── */}
        <section style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Link to="/login" className="btn btn-primary" onClick={() => track('onboarding_continue_login')}>
            Continue to Login
          </Link>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleActivate}
            disabled={busy || !isConfigured}
          >
            Activate Your Invitation
          </button>
        </section>
        {error && (
          <div role="alert" className="card" style={{ ...sectionStyle, padding: 'var(--space-3) var(--space-4)' }}>
            <p style={{ ...pStyle, color: 'var(--color-white)' }}>{error}</p>
          </div>
        )}

        {/* ── 1. What Is Rentava ────────────────────────────────────── */}
        <section className="card" style={sectionStyle} aria-labelledby="ob-what">
          <h2 id="ob-what" style={h2Style}>What is Rentava</h2>
          <p style={pStyle}>
            <strong>Rentava is an Opportunity Intelligence platform</strong> for RV park and
            short-term rental (STR) investors. It helps you find, evaluate, and prioritize
            acquisition opportunities — surfacing where to spend your time, not just running
            calculations.
          </p>
          <ul style={{ ...pStyle, paddingLeft: 'var(--space-5)', display: 'grid', gap: 'var(--space-1)' }}>
            <li><strong>RV park acquisitions</strong> — evaluate parks and campgrounds as investments.</li>
            <li><strong>STR acquisitions</strong> — analyze short-term rental opportunities by market.</li>
            <li><strong>Market analysis</strong> — live market data plus Rentava&apos;s derived intelligence.</li>
            <li><strong>Opportunity qualification</strong> — fast triage so you focus on the best deals.</li>
          </ul>
        </section>

        {/* ── 2. Opportunity Signals ────────────────────────────────── */}
        <section className="card" style={sectionStyle} aria-labelledby="ob-signals">
          <h2 id="ob-signals" style={h2Style}>Opportunity Signals</h2>
          <p style={pStyle}>
            When you triage an opportunity, Rentava resolves its read into one of three
            <strong> Opportunity Signals</strong>:
          </p>
          {SIGNALS.map((s) => (
            <div className="card" key={s.label} style={itemStyle}>
              <span style={{ fontWeight: 'var(--weight-bold)' }}>{s.label}</span>
              <span style={pStyle}>{s.body}</span>
            </div>
          ))}
          <p style={pStyle}>
            <strong>Opportunity intelligence, not investment advice.</strong> Signals and scores
            are informational inputs to help you think — they are not financial advice and not a
            recommendation to buy or sell. You own your investment decisions and your due diligence.
          </p>
        </section>

        {/* ── 3. Controlled Onboarding ──────────────────────────────── */}
        <section className="card" style={sectionStyle} aria-labelledby="ob-controlled">
          <h2 id="ob-controlled" style={h2Style}>Controlled onboarding — what to expect</h2>
          {/* BL-038 Story 38.4 — communicate the trial terms up front (sourced
              from the BL-040/041 catalog). Trial is a STATUS, not a tier. */}
          <p style={pStyle}>
            Your invitation includes a <strong>7-day trial</strong> with up to{' '}
            <strong>{TRIAL_LIMITS.analysis_volume} analyses</strong> — full access
            for <strong>7 days OR {TRIAL_LIMITS.analysis_volume} analyses, whichever
            comes first</strong>. No card required.
          </p>
          {EXPECTATIONS.map((e) => (
            <div className="card" key={e.title} style={itemStyle}>
              <span style={{ fontWeight: 'var(--weight-bold)' }}>{e.title}</span>
              <span style={pStyle}>{e.body}</span>
            </div>
          ))}
        </section>

        {/* ── 4. Help & Support (BL-037 reuse) ──────────────────────── */}
        <section className="card" style={sectionStyle} aria-labelledby="ob-help">
          <h2 id="ob-help" style={h2Style}>Help &amp; support</h2>
          <p style={pStyle}>
            Once you&apos;re in, the{' '}
            <Link to="/help" style={{ color: 'var(--color-teal)', fontWeight: 'var(--weight-semibold)' }}>
              Help Center
            </Link>{' '}
            answers the most common questions, and{' '}
            <Link to="/support" style={{ color: 'var(--color-teal)', fontWeight: 'var(--weight-semibold)' }}>
              Help &amp; Feedback
            </Link>{' '}
            is where you report a bug, request a feature, or ask a question — straight to the team.
          </p>
        </section>

        {/* ── Footer CTAs + disclaimer ──────────────────────────────── */}
        <section style={{ display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Link to="/login" className="btn btn-primary" onClick={() => track('onboarding_continue_login')}>
              Continue to Login
            </Link>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleActivate}
              disabled={busy || !isConfigured}
            >
              Activate Your Invitation
            </button>
          </div>
          <p style={{ ...pStyle, fontSize: 'var(--text-sm)', color: 'var(--color-steel)' }}>
            Rentava provides opportunity intelligence, not investment advice. Access is invite-only;
            activating your invitation signs you in with the Google account on your invitation.
          </p>
        </section>
      </main>
    </div>
  );
}
